<template>
	<section class="new-event-local-tours">
		<wait-modal id="new-local-tour-wait-modal" ref="waitModal" :title="titleLabel"></wait-modal>
		<conclusion-modal id="new-local-tour-conclusion-modal" ref="conclusionModal"></conclusion-modal>

		<div class="new-event-local-tours__inner">
			<h2>{{titleLabel}}</h2>

			<div v-if="sortedTours.length" class="new-event-local-tours__list">
				<article
					v-for="tour in sortedTours"
					:key="tour.id"
					class="new-event-local-tours__card"
					:class="{'new-event-local-tours__card--open': isSelectedTour(tour)}"
				>
					<div class="new-event-local-tours__header">
						<img
							class="new-event-local-tours__image"
							:src="imageFor(tour)"
							:alt="nameFor(tour)"
						>

						<div class="new-event-local-tours__summary">
							<h3>{{nameFor(tour)}}</h3>
							<p>
								{{translation.priceText}}:
								<strong>{{formatCurrency(priceFor(tour).price, priceFor(tour).priceCurrency)}}</strong>
								{{perPersonLabel}}
							</p>
						</div>

						<div class="new-event-local-tours__action">
							<button
								type="button"
								class="new-event-local-tours__button"
								:class="{'new-event-local-tours__button--outline': isSelectedTour(tour)}"
								@click="toggleTourForm(tour)"
							>
								{{selectButtonLabel(tour)}}
							</button>
						</div>
					</div>

					<div v-if="descriptionFor(tour)" class="new-event-local-tours__description">
						<p>{{descriptionFor(tour)}}</p>
					</div>

					<local-tour-reservation-form
						v-if="isSelectedTour(tour)"
						:event="event"
						:tour="tour"
						:countries="countries"
						@reserve="reserveTour"
					/>
				</article>
			</div>

			<p v-else-if="isLoaded" class="new-event-local-tours__no-results">
				{{noResultLabel}}
			</p>
		</div>
	</section>
</template>

<script>
import moment from 'moment';
import { translate } from '../../assets/translations';
import BookingService from '../../services/BookingService';
import ConclusionModal from '../shared/ConclusionModal.vue';
import DestinationService from '../../services/DestinationService';
import EventService from '../../services/EventService';
import FileRequest from '../../model/FileRequest';
import LocalTourReservationForm from './LocalTourReservationForm.vue';
import ServicePricesWebservice from '../../services/ServicePricesWebservice';
import WaitModal from '../shared/WaitModal.vue';
import { getFormatedDate, getStorageUrl } from '../../util';

const FALLBACK_TOUR_IMAGE = '/static/riodejaneiro.jpg';

export default {
	components: {
		ConclusionModal,
		LocalTourReservationForm,
		WaitModal,
	},
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			countries: [],
			eventService: new EventService(),
			isLoaded: false,
			selectedTourId: null,
			tours: [],
		};
	},
	computed: {
		cityName() {
			return this.event && this.event.destination ? this.event.destination.name : '';
		},
		closeLabel() {
			const labels = {
				Pt: 'fechar',
				En: 'close',
				Sp: 'cerrar',
			};
			return labels[this.$store.state.language.symbol] || labels.Pt;
		},
		noResultLabel() {
			const labels = {
				Pt: 'Nenhum resultado encontrado',
				En: 'No results found',
				Sp: 'Ningun resultado encontrado',
			};
			return this.translation.noResult || labels[this.$store.state.language.symbol] || labels.Pt;
		},
		perPersonLabel() {
			const labels = {
				Pt: 'por pessoa',
				En: 'per person',
				Sp: 'por persona',
			};
			return labels[this.$store.state.language.symbol] || labels.Pt;
		},
		sortedTours() {
			return this.tours.slice().sort((firstTour, secondTour) => (
				this.normalizeCurrencyValue(this.priceFor(firstTour).price)
				- this.normalizeCurrencyValue(this.priceFor(secondTour).price)
			));
		},
		titleLabel() {
			const title = String(this.translation.title || 'Tours Locais em')
				.replace(/^Tour Locais/, 'Tours Locais');
			return `${title} ${this.cityName}`.trim();
		},
		translation() {
			return translate(this.$store.state.language).localTour;
		},
	},
	mounted() {
		this.loadCountries();
		this.loadLocalTours();
	},
	methods: {
		buildTourBooking(booking, tour) {
			const price = this.priceFor(tour);
			return Object.assign({}, booking, {
				adultsQty: Number(booking.adultsQty) || 1,
				date: this.formatBookingDate(booking.date),
				guests: booking.guests.map(guest => Object.assign({}, guest)),
				serviceId: tour.id,
				serviceName: this.nameFor(tour),
				price: price.price,
				currency: price.priceCurrency,
			});
		},
		currentLanguageId() {
			return this.$store.state.language.id;
		},
		descriptionFor(tour) {
			const translation = this.translationFor(tour);
			return translation.description || tour.description || '';
		},
		extractToursFromResponse(serviceTypes, eventTourIds) {
			const selectedIds = eventTourIds.map(id => String(id));
			const tours = [];
			const addedIds = [];

			for (let typeIndex in serviceTypes) {
				const serviceType = serviceTypes[typeIndex];
				const services = serviceType && serviceType.services ? serviceType.services : [];

				for (let serviceIndex in services) {
					const service = services[serviceIndex];
					const serviceId = String(service.id);
					if (selectedIds.indexOf(serviceId) !== -1 && addedIds.indexOf(serviceId) === -1) {
						tours.push(service);
						addedIds.push(serviceId);
					}
				}
			}

			return tours;
		},
		formatBookingDate(date) {
			if (!date) return null;
			const dateText = String(date);
			if (dateText.indexOf('/') !== -1) return dateText;
			return moment(date).format('DD/MM/YYYY');
		},
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		imageFor(tour) {
			if (!tour || !tour.imagePath) return FALLBACK_TOUR_IMAGE;
			if (/^https?:\/\//i.test(tour.imagePath) || tour.imagePath.charAt(0) === '/') {
				return tour.imagePath;
			}
			return getStorageUrl(tour.imagePath);
		},
		isSelectedTour(tour) {
			return Boolean(tour && this.selectedTourId === tour.id);
		},
		async loadCountries() {
			if (!this.event || !this.event.client) return;

			try {
				const destinationService = new DestinationService(this.event.client);
				const response = await destinationService.getCountries();
				this.countries = response.data.sort((firstCountry, secondCountry) => {
					if (firstCountry.name > secondCountry.name) return 1;
					if (firstCountry.name < secondCountry.name) return -1;
					return 0;
				});
			} catch (error) {
				this.countries = [];
			}
		},
		async loadLocalTours() {
			if (!this.event || !this.event.id || !this.event.client) return;

			this.isLoaded = false;
			this.tours = [];
			if (this.$refs.waitModal) this.$refs.waitModal.open();

			try {
				const eventToursResponse = await this.eventService.getTours(this.event.id);
				const eventTourIds = eventToursResponse.data
					.map(eventTour => (eventTour.service ? eventTour.service.id : null))
					.filter(Boolean);

				if (!eventTourIds.length) {
					return;
				}

				const search = {
					clientId: this.event.client.id,
					cityId: this.event.destination ? this.event.destination.id : null,
					date: getFormatedDate(this.event.startDate),
					adultsQty: 1,
					serviceIds: eventTourIds,
					publishedRatesOnly: false
				};
				const servicePricesWebservice = new ServicePricesWebservice(this.event.client);
				const response = await servicePricesWebservice.listToursPrices(search);
				this.tours = this.extractToursFromResponse(response.data.serviceTypes || [], eventTourIds);
			} catch (error) {
				this.tours = [];
				this.$refs.conclusionModal.open(false, this.translation.fail.title, this.translation.fail.message);
			} finally {
				this.isLoaded = true;
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		nameFor(tour) {
			const translation = this.translationFor(tour);
			return translation.name || tour.name || '';
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
		priceFor(tour) {
			return tour && tour.prices && tour.prices.length
				? tour.prices[0]
				: { price: 0, priceCurrency: { symbol: 'R$' } };
		},
		async reserveTour({ tour, pax, booking }) {
			if (!this.event || !this.event.client) return;

			if (this.$refs.waitModal) this.$refs.waitModal.open();

			try {
				const bookingService = new BookingService(this.event.client);
				const tourBooking = this.buildTourBooking(booking, tour);
				const fileRequest = new FileRequest(pax, null, [tourBooking], 'service');
				await bookingService.save(fileRequest);
				this.selectedTourId = null;
				this.$refs.conclusionModal.open(
					true,
					this.translation.form.success.title,
					this.translation.form.success.message,
				);
			} catch (error) {
				this.$refs.conclusionModal.open(
					false,
					this.translation.form.fail.title,
					this.translation.form.fail.message,
				);
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		selectButtonLabel(tour) {
			return this.isSelectedTour(tour)
				? this.closeLabel
				: this.lowercaseLabel(this.translation.selectOption);
		},
		toggleTourForm(tour) {
			if (this.isSelectedTour(tour)) {
				this.selectedTourId = null;
				return;
			}
			this.selectedTourId = tour.id;
		},
		translationFor(tour) {
			if (!tour || !tour.translations || !tour.translations.length) return {};
			return tour.translations.find(translation => translation.language.id === this.currentLanguageId())
				|| tour.translations[0]
				|| {};
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-local-tours {
	background: #ffffff;
	color: #002c41;
}

.new-event-local-tours__inner {
	box-sizing: border-box;
	width: calc(100% - 196px);
	max-width: 1530px;
	margin: 0 auto 142px;
}

.new-event-local-tours__inner h2 {
	margin: 0 0 54px 10px;
	color: #002c41;
	font-size: 22px;
	font-weight: 900;
	line-height: 26px;
}

.new-event-local-tours__list {
	display: flex;
	flex-direction: column;
	gap: 34px;
}

.new-event-local-tours__card {
	box-sizing: border-box;
	width: 100%;
	overflow: hidden;
	padding: 39px 53px 58px;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
}

.new-event-local-tours__header {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 70px;
	padding-bottom: 46px;
	border-bottom: 1px solid #d7d7d7;
}

.new-event-local-tours__image {
	display: block;
	flex: 0 1 348px;
	width: 100%;
	max-width: 348px;
	height: 231px;
	border-radius: 25px;
	object-fit: cover;
}

.new-event-local-tours__summary {
	flex: 1 1 330px;
	min-width: 0;
}

.new-event-local-tours__summary h3 {
	max-width: 520px;
	margin: 0 0 64px;
	color: #002c41;
	font-size: 25px;
	font-weight: 700;
	line-height: 29px;
}

.new-event-local-tours__summary p {
	margin: 0;
	color: #5a5a5a;
	font-size: 20px;
	font-weight: 400;
	line-height: 25px;
}

.new-event-local-tours__summary strong {
	color: #002c41;
	font-weight: 700;
}

.new-event-local-tours__action {
	display: flex;
	flex: 0 0 220px;
	justify-content: flex-end;
}

.new-event-local-tours__button {
	box-sizing: border-box;
	width: 200px;
	height: 34px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-local-tours__button--outline {
	background: #ffffff;
	color: #ff9600;
}

.new-event-local-tours__description {
	margin: 45px 32px 0;
	color: #5a5a5a;
	font-size: 18px;
	font-weight: 700;
	line-height: 35px;
	white-space: pre-line;
}

.new-event-local-tours__description p {
	margin: 0;
}

.new-event-local-tours__no-results {
	box-sizing: border-box;
	margin: 0;
	padding: 36px 52px;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
	color: #002c41;
	font-size: 20px;
	font-weight: 700;
	line-height: 24px;
}

@media (max-width: 1100px) {
	.new-event-local-tours__header {
		gap: 38px;
	}

	.new-event-local-tours__image {
		flex-basis: 320px;
		max-width: 320px;
	}

	.new-event-local-tours__action {
		justify-content: flex-start;
	}

	.new-event-local-tours__summary h3 {
		margin-bottom: 32px;
	}
}

@media (max-width: 900px) {
	.new-event-local-tours__inner {
		width: 90%;
		margin-bottom: 72px;
	}

	.new-event-local-tours__inner h2 {
		margin: 0 0 28px;
		font-size: 20px;
		line-height: 24px;
	}

	.new-event-local-tours__card {
		padding: 24px;
		border-radius: 24px;
	}

	.new-event-local-tours__header {
		flex-direction: column;
		align-items: stretch;
		gap: 24px;
		padding-bottom: 30px;
	}

	.new-event-local-tours__image {
		flex-basis: auto;
		max-width: none;
		height: auto;
		aspect-ratio: 348 / 231;
		border-radius: 18px;
	}

	.new-event-local-tours__summary h3 {
		margin-bottom: 22px;
		font-size: 21px;
		line-height: 26px;
	}

	.new-event-local-tours__summary p,
	.new-event-local-tours__description {
		font-size: 16px;
		line-height: 28px;
	}

	.new-event-local-tours__action {
		flex-basis: auto;
	}

	.new-event-local-tours__button {
		width: 100%;
		max-width: 200px;
	}

	.new-event-local-tours__description {
		margin: 30px 0 0;
	}

	.new-event-local-tours__no-results {
		padding: 26px 24px;
		border-radius: 24px;
		font-size: 17px;
		line-height: 22px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/local-tour/LocalTourPrice.vue